import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useUserHasPermission } from '@/hooks/usePermissions';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import { getListedTemplatesPage } from '@payaca/store/templates/templatesActions';
import { SortBy } from '@payaca/types/listedTemplateTypes';
import { SortDirection } from '@payaca/types/listViewTypes';

/**
 * Decides whether the create-a-Proposal flow should offer a template chooser or
 * create the Proposal straight away, based on the user's permissions and
 * whether the account has any Proposal templates.
 *
 * @param enabled - probe only when creating a new Proposal (skip in edit mode)
 */
export const useProposalTemplateChoice = (enabled = true) => {
  const dispatch = useDispatch();

  const hasUseProposalTemplatesPermission = useUserHasPermission({
    permissions: [TemplatesPermissions.USE_PROPOSAL_TEMPLATES],
  });
  const canAddCustomJob = useUserHasPermission({
    permissions: [JobPermissions.ADD_CUSTOM_JOB],
  });
  const canAddJob = useUserHasPermission({
    permissions: [JobPermissions.ADD_JOB],
  });

  const [hasAnyTemplates, setHasAnyTemplates] = useState(false);
  const [hasCheckedTemplates, setHasCheckedTemplates] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!hasUseProposalTemplatesPermission) {
      setHasCheckedTemplates(true);
      return;
    }

    dispatch(
      getListedTemplatesPage.request({
        queryParams: {
          pageSize: 1,
          pageNumber: 1,
          entityTypes: ['proposal'],
          sortBy: SortBy.NAME,
          sortDirection: SortDirection.ASCENDING,
        },
        callback: (page) => {
          setHasAnyTemplates(!!page.items.length);
          setHasCheckedTemplates(true);
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- dispatch is stable
  }, [enabled, hasUseProposalTemplatesPermission]);

  // A user who can add Proposals but not custom (from-scratch) ones must pick a
  // template, so the chooser is shown even when the account has none.
  const cannotCreateCustomJob = !canAddCustomJob && canAddJob;

  const showTemplateSelectionModal =
    enabled &&
    hasUseProposalTemplatesPermission &&
    (hasAnyTemplates || cannotCreateCustomJob);

  return {
    hasCheckedTemplates,
    hasAnyTemplates,
    showTemplateSelectionModal,
  };
};
